import React from 'react';
import { FormControl, FormControlLabel, Checkbox, Box } from '@mui/material';
// Import the extracted section components
import ABCDataSection from './components/ABCDataSection';
import PhaseEventSections from './components/PhaseEventSections';
import TimeOfCollectionSection from './components/TimeOfCollectionSection';
import PromptingLevelSection from './components/PromptingLevelSection';
import StaffPresentInfoSection from './components/StaffPresentInfoSection';
import CustomSectionsWrapper from './components/CustomSectionsWrapper';
// Import the extracted field components
import SessionSummaryField from './components/SessionSummaryField';
import GroupSizeField from './components/GroupSizeField';
import SessionLengthField from './components/SessionLengthField';
import CPTCodesField from './components/CPTCodesField';
import PrecedingBehaviorSection from './components/PrecedingBehaviorSection';

const DataPointOptions = ({
  dataPoint,
  index,
  handleCheckboxChange,
  handleAbcChange,
  handleFieldChange,
  handleTimeChange,
  allGoalData,
  user,
  customSections = [],
  isLoadingCustomSections = false,
}) => {
  // Handler for custom section checkbox changes
  const handleCustomSectionCheckboxChange = (index, event) => {
    const value = event.target.value;
    const checked = event.target.checked;
    // Defensive: ensure selectedOptions is always an array
    const prevSelected = Array.isArray(dataPoint.selectedOptions)
      ? dataPoint.selectedOptions
      : [];
    let newSelected;
    if (checked) {
      // Add if not already present
      newSelected = prevSelected.includes(value)
        ? prevSelected
        : [...prevSelected, value];
    } else {
      // Remove if present
      newSelected = prevSelected.filter((v) => v !== value);

      // Also clear the custom section data when unchecking
      const currentCustomSection = dataPoint.customSection || [];
      const updatedCustomSection = currentCustomSection.filter(
        (section) => section.docId !== value
      );
      handleFieldChange(index, 'customSection', updatedCustomSection);
    }
    handleFieldChange(index, 'selectedOptions', newSelected);
  };

  // When answers inside a custom section change, update the corresponding field in the datapoint
  const handleCustomSectionAnswerChange = (updatedCustomSection) => {
    handleFieldChange(index, 'customSection', updatedCustomSection || []);
  };

  return (
    <FormControl
      component='fieldset'
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <ABCDataSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleAbcChange={handleAbcChange}
        allGoalData={allGoalData}
      />

      <PrecedingBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
      />

      <PhaseEventSections
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
      />

      <TimeOfCollectionSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        handleTimeChange={handleTimeChange}
        allGoalData={allGoalData}
      />

      <PromptingLevelSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        allGoalData={allGoalData}
      />

      {(allGoalData?.DataEntrySelection?.DataEntrySelection?.includes(
        'Add Session Summary'
      ) ||
        dataPoint?.selectedOptions?.includes('Add Session Summary')) && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint?.selectedOptions?.includes(
                  'Add Session Summary'
                )}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Session Summary'
              />
            }
            label='Add Session Summary'
          />
          {dataPoint?.selectedOptions?.includes('Add Session Summary') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <SessionSummaryField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {(allGoalData?.DataEntrySelection?.DataEntrySelection?.includes(
        'Add Group Size'
      ) ||
        dataPoint?.selectedOptions?.includes('Add Group Size')) && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint?.selectedOptions?.includes('Add Group Size')}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Group Size'
              />
            }
            label='Add Group Size'
          />
          {dataPoint?.selectedOptions?.includes('Add Group Size') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <GroupSizeField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {(allGoalData?.DataEntrySelection?.DataEntrySelection?.includes(
        'Add Session Length'
      ) ||
        dataPoint?.selectedOptions?.includes('Add Session Length')) && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint?.selectedOptions?.includes(
                  'Add Session Length'
                )}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Session Length'
              />
            }
            label='Add Session Length'
          />
          {dataPoint?.selectedOptions?.includes('Add Session Length') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <SessionLengthField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {(allGoalData?.DataEntrySelection?.DataEntrySelection?.includes(
        'Add CPT Code'
      ) ||
        dataPoint?.selectedOptions?.includes('Add CPT Code')) && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint?.selectedOptions?.includes('Add CPT Code')}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add CPT Code'
              />
            }
            label='Add CPT Code'
          />
          {dataPoint?.selectedOptions?.includes('Add CPT Code') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <CPTCodesField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
                handleCheckboxChange={handleCheckboxChange}
              />
            </Box>
          )}
        </>
      )}

      <StaffPresentInfoSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        allGoalData={allGoalData}
      />

      {/* Custom Data Entry Sections */}
      <CustomSectionsWrapper
        dataPoint={dataPoint}
        index={index}
        allGoalData={allGoalData}
        user={user}
        customSections={customSections}
        isLoadingCustomSections={isLoadingCustomSections}
        handleCustomSectionCheckboxChange={handleCustomSectionCheckboxChange}
        handleCustomSectionAnswerChange={handleCustomSectionAnswerChange}
      />
    </FormControl>
  );
};

export default DataPointOptions;
